<script lang="tsx">
  import { set } from 'lodash'
  import { PropType, defineComponent } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import { BeerStyleObj } from '@xhs/yam-beer/core'
  import IconTooltip from '@xhs/yam-beer/cmp/overlayers/Tooltip/IconTooltip.vue'
  import initStyle from './style'

  initStyle()

type Layout = 'table' | 'block' | 'inline'

  function renderTrailingText(
    { readonly, errorText, helperText }
  : { readonly: boolean; errorText: string|undefined; helperText: string|undefined },
  ) {
    if (readonly) {
      return null
    }
    if (errorText) {
      return (
      <NewBsBox bs={{ 'cmp.formGroup': 'error' }}>
        {errorText}
      </NewBsBox>
    )
    }
    if (helperText) {
      return (
      <NewBsBox bs={{ 'cmp.formGroup': 'helper' }}>
        {helperText}
      </NewBsBox>
    )
    }
    return null
  }

  const GroupLabel = (
    {
      label, layout, bs,
      } : {
    label: string|undefined
    layout: Layout
    bs: BeerStyleObj
  },
    { slots } : { slots: any },
  ) => {
    if (!label) {
      return null
    }
    return (
    <NewBsBox
      bs={{
        'cmp.formGroup': 'labelText',
        'cmp.formGroup.labelText.layout': layout,
        ...bs,
      }}
    >
      <NewBsBox bs={{
        display: 'flex',
        alignItems: 'flex-start',
        whiteSpace: 'nowrap',
      }}>
        {label}
        {slots.default?.()}
      </NewBsBox>
    </NewBsBox>
  )
  }

  export default defineComponent({
    name: 'FormGroup',
    components: {
      NewBsBox,
    },
    props: {
      /**
       * 有时候可以设置 as='label', 这样点击整个区域的任何地方都能聚焦到 input 上面
       */
      as: {
        type: String,
        default: 'div',
      },
      /**
       * 容器 bs
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * label bs
       */
      labelBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 是否只读
       */
      readonly: {
        type: Boolean,
        default: false,
      },
      /**
       * control slot + error 的容器 bs
       */
      controlBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 是否必填
       */
      required: {
        type: Boolean,
        default: false,
      },
      /**
       * 布局方式，block|inline|table
       */
      layout: {
        type: String as PropType<Layout>,
        default: 'block',
      },
      /**
       * label 文字
       */
      label: {
        type: String,
      },
      /**
       * 错误描述
       */
      errorText: {
        type: String,
      },
      /**
       * 帮助提示信息，跟着 input 走
       * 和 labelInfo 相反，显示在后
       */
      helperText: {
        type: String,
      },
      /**
       * label 描述，跟着label 走，显示在前
       */
      labelInfo: {
        type: String,
      },
      /**
       * label tooltip
       */
      labelTooltip: {
        type: Object,
      },
    },
    setup(props, { attrs, slots }) {
      return () => {
        const {
          layout, as, bs, label, labelBs,
          required, controlBs, readonly, errorText,
          labelInfo, labelTooltip,
          helperText,
        } = props
        const sls = slots.default?.() || []
        if (readonly) {
          sls.forEach(n => {
            set(n, 'props.readonly', true)
          })
        } else if (errorText) {
          sls.forEach(n => {
            set(n, 'props.data-error', errorText ? 'true' : null)
          })
        }
        return (
        <NewBsBox
          bs={{
            'cmp.formGroup.layout': layout,
            ...bs,
          }}
          as={as}
          {...attrs}
        >
          <GroupLabel
            label={label}
            layout={layout}
            bs={labelBs}
          >
            {!readonly && required && (
              <NewBsBox
                as="span"
                bs={{ 'cmp.formGroup': 'requiredSymbol' }}
                {...{
                  role: 'img',
                  'aria-label': 'required',
                }}>
                *
              </NewBsBox>
            )}
            {!readonly && labelTooltip && (
                <NewBsBox
                  bs={{ 'cmp.formGroup.labelText': 'tooltip' }}
                >
                  <IconTooltip {...labelTooltip} />
                </NewBsBox>
            )}
            { labelInfo && (
              <NewBsBox bs={{ display: 'inline-block', ml: 12, color: 'mute' }}>
                {labelInfo}
              </NewBsBox>
            )}
          </GroupLabel>
          <NewBsBox bs={{
            pb: layout === 'inline' ? 0 : 24,
            ...controlBs,
          }}>
            { readonly ? (
              <NewBsBox bs={{
                fontSize: '14px',
                lineHeight: '32px',
              }}>
                {sls}
              </NewBsBox>
            ) : sls }
            {renderTrailingText({ readonly, errorText, helperText })}
          </NewBsBox>
        </NewBsBox>
      )
      }
    },
  })
</script>
